import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useMotionValueEvent, useReducedMotion } from 'framer-motion';
import { ArrowUpRight, Plus } from 'lucide-react';
import { Reveal, Chapter } from './StartShared';

const TechniqueLink = ({ item }) => {
  const props = { className: 'start-text-link', 'data-testid': `start-method-${item.id}-link` };
  const content = <>{item.cta}<ArrowUpRight size={18} aria-hidden="true" /></>;
  return item.href.includes('#') ? <a href={item.href} {...props}>{content}</a> : <Link to={item.href} {...props}>{content}</Link>;
};

export const StartTechniques = ({ copy }) => {
  const [active, setActive] = useState(0);
  const stories = useRef(null);
  const reduced = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: stories, offset: ['start center', 'end center'] });
  useMotionValueEvent(scrollYProgress, 'change', (progress) => setActive(Math.min(2, Math.max(0, Math.floor(progress * 3)))));
  const items = copy.techniques.items;

  return (
    <section id="techniken" className="start-techniques start-dark" aria-labelledby="techniques-heading" data-testid="start-methods">
      <div className="start-container">
        <Chapter number="02" label={copy.techniques.label} id="techniques-heading" />
        <Reveal className="start-section-intro">
          <p className="start-display" data-testid="start-methods-title">{copy.techniques.title[0]}<br /><span className="start-muted">{copy.techniques.title[1]}</span></p>
          <p className="start-intro-text" data-testid="start-methods-description">{copy.techniques.intro}</p>
        </Reveal>
        <div className="start-tech-layout">
          <div className="start-tech-sticky" data-testid="start-tech-sticky">
            <div className="start-tech-media" data-testid="start-tech-active-image" data-active-technique={items[active].id}>
              {items.map((item, index) => (
                <motion.img key={item.id} src={`/images/start/${item.image}.webp`} alt={item.alt} loading="lazy" width="900" height="600" initial={false} animate={{ opacity: active === index ? 1 : 0, scale: active === index || reduced ? 1 : 1.035 }} transition={{ duration: reduced ? 0 : 0.55 }} aria-hidden={active !== index} className={`start-tech-photo start-tech-photo-${item.id}`} data-testid={`start-tech-desktop-image-${item.id}`} />
              ))}
              <span className="start-tech-crosshair" aria-hidden="true"><Plus strokeWidth={1} size={30} /></span>
            </div>
            <div className="start-tech-caption start-mono" data-testid="start-tech-caption"><span>{items[active].caption}</span><span>0{active + 1} / 03</span></div>
            <nav className="start-tech-index" aria-label={copy.techniques.nav} data-testid="start-tech-index">
              {items.map((item, index) => <a href={`#technik-${item.id}`} key={item.id} aria-current={active === index ? 'step' : undefined} data-testid={`start-tech-select-${item.id}`}><span className="start-mono">0{index + 1}</span><span>{item.title}</span></a>)}
            </nav>
          </div>
          <div ref={stories} className="start-tech-stories">
            {items.map((item, index) => (
              <article id={`technik-${item.id}`} key={item.id} className="start-tech-story" data-testid={`start-method-${item.id}`}>
                <figure className="start-tech-mobile-media">
                  <img src={`/images/start/${item.image}.webp`} alt={item.alt} width="900" height="600" loading="lazy" className={`start-tech-photo-${item.id}`} data-testid={`start-tech-mobile-image-${item.id}`} />
                  <figcaption className="start-mono" data-testid={`start-tech-mobile-caption-${item.id}`}>{item.caption}</figcaption>
                </figure>
                <Reveal>
                  <span className="start-tech-number start-mono" data-testid={`start-method-number-${item.id}`}>0{index + 1} / 03</span>
                  <h3 data-testid={`start-method-title-${item.id}`}>{item.title}</h3>
                  <p className="start-tech-kicker" data-testid={`start-method-kicker-${item.id}`}>{item.kicker}</p>
                  <p className="start-tech-text" data-testid={`start-method-description-${item.id}`}>{item.text}</p>
                  <ul className="start-tech-details" data-testid={`start-method-details-${item.id}`}>
                    {item.details.map((detail) => <li key={detail}><Plus size={13} aria-hidden="true" />{detail}</li>)}
                  </ul>
                  <TechniqueLink item={item} />
                </Reveal>
              </article>
            ))}
          </div>
        </div>
        <p className="start-method-note" data-testid="start-method-suitability-note">{copy.techniques.note}</p>
      </div>
    </section>
  );
};